import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { ETabsStyle, ETabsVariant, Tabs } from '@/components/common/Hero/Tabs';
import { EHomeSearchWidgetVariant, HomeSearchWidget } from '@/components/ui/HomeSearchWidget';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ERideTransitionPlacement } from '@/services/analytics/ride';

import { useShowOneWayBanner } from './OneWayBanner/useShowOneWayBanner';

// We can't use sticky alone, since we can't render it at the root level.
// Making it fixed only when stuck, is adding some level of unresponsiveness.
// A combination of sticky and fixed is solving the issue, and allowing us
// to have the same behavior and render in the right place.
export const StickySearchWidget = () => {
  const { isAboveTablet } = useBreakpoint();

  // When the one-way banner renders directly below, tighten the bottom margin
  // so the banner sits close to the search widget instead of leaving a large gap.
  // Mirror the banner's exact visibility (flag + geo within a route-city radius)
  // so an out-of-range visitor doesn't leave a phantom gap for a banner that
  // never renders.
  const isOneWayBannerEnabled = useShowOneWayBanner();

  const containerRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    if (isAboveTablet || !containerRef.current) return setIsStuck(false);

    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!!entry && entry.intersectionRatio < 1),
      { rootMargin: '-1px 0px 0px 0px', threshold: [1] },
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [isAboveTablet]);

  return (
    <>
      <div
        ref={containerRef}
        className={cn('sticky top-0 z-20 md:relative', {
          'mb-2 lg:mb-4': isOneWayBannerEnabled,
          'mb-4 lg:mb-8': !isOneWayBannerEnabled,
        })}>
        <div className="-mx-6 md:mx-0 min-h-[64px] md:min-h-[48px] lg:min-h-[100px] xl:h-[100px]">
          <div
            // Only paint a background once the widget is stuck (fixed at the top,
            // where it must be opaque). At rest the widget sits on the hero's own
            // background, so painting an identical color here just adds a redundant
            // compositing layer whose edge shows as a faint seam on retina screens.
            style={isStuck ? { backgroundColor: 'var(--page-header-bg-color)' } : undefined}
            className={cn(
              'px-6 py-2 md:p-0 transition-[background-color] duration-700 ease-in-out',
              {
                'fixed top-0 left-0 right-0 z-1299 shadow-500': isStuck,
              },
            )}>
            <div className="flex flex-col items-center justify-between gap-4 xl:flex-row">
              <HomeSearchWidget variant={EHomeSearchWidgetVariant.RvRentals} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center my-4 lg:hidden">
        <Tabs
          tabVariant={ETabsVariant.Rentals}
          style={ETabsStyle.Links}
          ridePlacement={ERideTransitionPlacement.HOME_HERO_TABS}
        />
      </div>
    </>
  );
};
